/**
 * /docs — Developer documentation MVP.
 *
 * Single-page reference covering:
 *  - Quickstart (cURL the public dry-run endpoint)
 *  - Auth (JWT bearer)
 *  - Core endpoints (policy, billing, integrations)
 *  - Webhooks (Slack drift, Stripe billing)
 *  - Rate limits + error codes
 *  - RBAC + tenant model
 *
 * Strategic value (per Feb 24 audit):
 * "Devs don't trust a tool without docs. Even a one-page Mintlify-style
 * doc beats nothing."
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import BackToHomePill from '../components/BackToHomePill';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { BookOpen, Code2, Key, Zap, Webhook, ShieldCheck, Gauge, Copy, Check, ArrowRight, ChevronRight } from 'lucide-react';

const API_BASE = 'https://api.cartieai.com';

const SECTIONS = [
  { id: 'quickstart', label: 'Quickstart', icon: Zap },
  { id: 'auth', label: 'Authentication', icon: Key },
  { id: 'endpoints', label: 'Core Endpoints', icon: Code2 },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'rate-limits', label: 'Rate Limits', icon: Gauge },
  { id: 'rbac', label: 'RBAC & Tenants', icon: ShieldCheck },
];

const CodeBlock = ({ children, lang = 'bash' }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative group rounded-lg border border-white/10 bg-slate-950 overflow-hidden my-3">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5 text-[11px]">
        <span className="text-slate-400 font-mono uppercase tracking-widest">{lang}</span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-slate-400 hover:text-emerald-300 transition-colors"
          data-testid="docs-copy-btn"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 text-[12px] leading-snug text-emerald-200 font-mono">{children}</pre>
    </div>
  );
};

const Endpoint = ({ method, path, desc }) => (
  <div className="flex items-start gap-3 py-2 px-3 rounded-md hover:bg-emerald-500/5 transition-colors">
    <span className={`text-[11px] font-bold font-mono uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0 ${
      method === 'GET' ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/30' :
      method === 'POST' ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/30' :
      method === 'PATCH' ? 'text-amber-300 bg-amber-500/10 border border-amber-500/30' :
      'text-rose-300 bg-rose-500/10 border border-rose-500/30'
    }`}>{method}</span>
    <div className="min-w-0 flex-1">
      <code className="text-sm text-white font-mono">{path}</code>
      <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
    </div>
  </div>
);

export default function Docs() {
  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="Developer Docs · CARTIEAI — API, webhooks, RBAC, rate limits"
        description="Complete developer reference for CARTIEAI: authentication, core endpoints, webhooks, rate limits, RBAC, and tenant model. No contact-sales walls."
        canonical="/docs"
        ogSlug="docs"
      />
      <BackToHomePill />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/10 blur-[120px] -z-10" />
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-10">
          <Badge className="bg-emerald-500/10 text-emerald-300 border-0 font-mono uppercase tracking-[0.18em] mb-5">
            <BookOpen className="w-3 h-3 mr-1.5" /> Developer Docs · v1
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" data-testid="docs-h1">
            Build on CARTIEAI in <span className="text-emerald-300">60 seconds.</span>
          </h1>
          <p className="text-lg text-slate-300 mt-5 max-w-3xl">
            Quickstart, auth, endpoints, webhooks. No contact-sales walls — every endpoint is documented here.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[220px,1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-3">On this page</div>
          <nav className="space-y-1">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`docs-toc-${s.id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/5 transition-colors"
              >
                <s.icon className="w-3.5 h-3.5" />
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="space-y-14 min-w-0">
          {/* Quickstart */}
          <section id="quickstart" data-testid="docs-quickstart">
            <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Quickstart</h2>
            <p className="text-slate-300 mb-3">Try the public Rego policy engine in a single curl — no signup needed.</p>
            <CodeBlock>{`curl -X POST ${API_BASE}/api/policy/public-dry-run \\
  -H "Content-Type: application/json" \\
  -d '{
    "rego_source": "package cartie.test\\n\\ndefault allow := false\\n\\nallow if input.cost <= 100",
    "input": { "cost": 50 }
  }'`}</CodeBlock>
            <p className="text-xs text-slate-400">Response: <code className="text-emerald-300 font-mono">{'{ "allow": true, "violations": [], "warnings": [], "package": "cartie.test" }'}</code></p>
          </section>

          {/* Auth */}
          <section id="auth" data-testid="docs-auth">
            <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Authentication</h2>
            <p className="text-slate-300 mb-3">
              All non-public endpoints use a JWT bearer token. Generate one at <Link to="/developers" className="text-emerald-400 hover:text-emerald-300 underline-offset-4 hover:underline">/developers</Link> after signing in.
            </p>
            <CodeBlock>{`curl -X GET ${API_BASE}/api/me/org \\
  -H "Authorization: Bearer YOUR_TOKEN_HERE"`}</CodeBlock>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3">
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-widest mb-1">Token lifetime</div>
                <p className="text-sm text-slate-300">7 days. Refresh via <code className="text-emerald-300 font-mono text-xs">POST /api/auth/refresh</code></p>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3">
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-widest mb-1">Scopes</div>
                <p className="text-sm text-slate-300">Bound to the org of the user. Cross-tenant access is blocked at the JWT level.</p>
              </div>
            </div>
          </section>

          {/* Core Endpoints */}
          <section id="endpoints" data-testid="docs-endpoints">
            <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Core Endpoints</h2>
            <p className="text-slate-300 mb-3">The 12 endpoints you&apos;ll use 90% of the time. See <Link to="/developers" className="text-emerald-400 hover:text-emerald-300 underline-offset-4 hover:underline">/developers</Link> for the full surface.</p>
            <div className="rounded-xl border border-white/10 bg-slate-900/60 divide-y divide-white/5">
              <Endpoint method="POST" path="/api/policy/public-dry-run" desc="Evaluate Rego against an input — public, no auth, capped 64KB" />
              <Endpoint method="POST" path="/api/policy/dry-run" desc="Authed dry-run with org-scoped policies + audit trail" />
              <Endpoint method="GET" path="/api/policy/templates" desc="Built-in starter Rego templates (cost-center tag, budget cap, region allowlist)" />
              <Endpoint method="GET" path="/api/me/org" desc="Current org info — plan, slug, role" />
              <Endpoint method="GET" path="/api/dashboard/summary" desc="KPI cards for the auth user's tenant" />
              <Endpoint method="GET" path="/api/cost-centers" desc="Hierarchical cost-center tree" />
              <Endpoint method="POST" path="/api/integrations/aws/connect" desc="OAuth-style AWS read-only role assume" />
              <Endpoint method="GET" path="/api/landing-demo/summary" desc="Demo-tracking funnel stats (admin only)" />
              <Endpoint method="GET" path="/api/pilot-activation/readiness-score" desc="Public SOC 2 readiness · 6/6 controls" />
              <Endpoint method="GET" path="/api/public/changelog" desc="Public changelog · sourced from PRD.md" />
              <Endpoint method="POST" path="/api/integrations/slack/drift-action" desc="Slack block_actions callback (acknowledge / snooze)" />
              <Endpoint method="GET" path="/api/og/{slug}.png" desc="Branded OG card for a route — used by all marketing pages" />
            </div>
          </section>

          {/* Webhooks */}
          <section id="webhooks" data-testid="docs-webhooks">
            <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Webhooks</h2>
            <p className="text-slate-300 mb-3">All events use signed HMAC headers. Verify in 4 lines:</p>
            <CodeBlock lang="python">{`import hmac, hashlib
def verify(secret: str, body: bytes, signature: str) -> bool:
    digest = hmac.new(secret.encode(), body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(digest, signature)`}</CodeBlock>
            <div className="rounded-xl border border-white/10 bg-slate-900/60 divide-y divide-white/5 mt-3">
              <Endpoint method="POST" path="cartie.drift.alert" desc="Cost drift detected — actionable Slack block_actions payload" />
              <Endpoint method="POST" path="cartie.policy.violation" desc="Rego policy denied a request — full audit context" />
              <Endpoint method="POST" path="cartie.budget.breach" desc="Cost-center exceeded its envelope at PR-time" />
              <Endpoint method="POST" path="cartie.week1.roi" desc="Week-1 ROI PDF ready — link expires in 30 days" />
            </div>
          </section>

          {/* Rate Limits */}
          <section id="rate-limits" data-testid="docs-rate-limits">
            <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Rate Limits</h2>
            <p className="text-slate-300 mb-3">Per-token + per-IP, sliding window. Headers tell you exactly where you stand:</p>
            <CodeBlock lang="http">{`HTTP/1.1 200 OK
X-RateLimit-Limit: 600
X-RateLimit-Remaining: 587
X-RateLimit-Reset: 1714499843`}</CodeBlock>
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-300 tabular-nums">600</div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">req/min · authed</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-300 tabular-nums">120</div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">req/min · public</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-300 tabular-nums">30s</div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">max request timeout</div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Need more? Enterprise tier removes the rate cap entirely.</p>
          </section>

          {/* RBAC */}
          <section id="rbac" data-testid="docs-rbac">
            <h2 className="text-2xl font-bold text-white tracking-tight mb-2">RBAC &amp; Tenant Model</h2>
            <p className="text-slate-300 mb-3">Every user belongs to exactly one org (multi-org per user is on the roadmap). Within an org, three roles:</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-widest mb-1">Owner</div>
                <p className="text-sm text-slate-300">Billing, members, integrations, policy authoring. Audit-logged.</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4">
                <div className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-1">Admin</div>
                <p className="text-sm text-slate-300">All read + write except billing &amp; ownership transfer.</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-slate-900/60 p-4">
                <div className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-1">Member</div>
                <p className="text-sm text-slate-300">Read-only dashboards + run free tools. Can&apos;t modify policies.</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">SCIM provisioning, custom roles, and per-cost-center scoped roles are on the Enterprise tier.</p>
          </section>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-slate-900/60 border border-emerald-500/30 p-8 shadow-md shadow-emerald-500/10" data-testid="docs-cta">
            <h3 className="text-2xl font-extrabold text-white tracking-tight">Wire it up?</h3>
            <p className="text-slate-300 mt-2 mb-6 max-w-2xl">14-day pilot · no card · founders reply in 4 hours.</p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/pricing">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5">
                  Start free pilot
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/policy-playground">
                <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12">
                  Try Policy Playground
                </Button>
              </Link>
              <Link to="/">
                <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12">
                  ← Back to home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
